import React, { createContext, useContext, useEffect, useState } from "react";
import { api, setToken, clearToken, captureSession, isSessionCurrent, sessionChangedError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionGeneration, setSessionGeneration] = useState(() => captureSession().generation);

  useEffect(() => {
    let active = true;
    let request = 0;
    const refreshSession = () => {
      const id = ++request;
      const scope = captureSession();
      setSessionGeneration(scope.generation);
      setUser(null);
      if (!scope.token) { setLoading(false); return; }
      setLoading(true);
      const current = () => active && request === id && isSessionCurrent(scope);
      api.get("/auth/me")
        .then((res) => { if (current()) setUser(res.data); })
        .catch(() => { if (current()) setUser(null); })
        .finally(() => { if (current()) setLoading(false); });
    };
    refreshSession();
    window.addEventListener("bbw:session-change", refreshSession);
    return () => { active = false; ++request; window.removeEventListener("bbw:session-change", refreshSession); };
  }, []);

  const login = async (email, password) => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    clearToken();
    setUser(null);
    const scope = captureSession();
    const res = await api.post("/auth/login", { email: normalizedEmail, password: String(password || "").trim() });
    if (!isSessionCurrent(scope)) throw sessionChangedError();
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, sessionGeneration }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
